import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Sparkles, 
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { insightsTr } from '../data/insights';
import { insightsEn } from '../data/insights.en';
import { projects as projectsTr } from '../data/projects';
import { projectsEn } from '../data/projects.en';
import type { Project } from '../types';

const InsightDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const isEn = language === 'EN';

  const dataset = isEn ? insightsEn : insightsTr;
  const projectsDataset = isEn ? projectsEn : projectsTr;

  const insight = dataset.find((item) => item.slug === slug);

  if (!insight) {
    return <Navigate to={isEn ? "/en/insights" : "/icgoruler"} replace />;
  }

  const canonicalUrl = isEn
    ? `https://www.we24agency.com/en/insights/${insight.slug}`
    : `https://www.we24agency.com/icgoruler/${insight.slug}`;
  const alternateTr = `https://www.we24agency.com/icgoruler/${insight.slug}`;
  const alternateEn = `https://www.we24agency.com/en/insights/${insight.slug}`;

  // Related projects resolved
  const relatedProjects = (insight.relatedProjectSlugs || [])
    .map(pSlug => projectsDataset.find((p: Project) => p.slug === pSlug || p.id === pSlug))
    .filter(Boolean);

  // Schema graph generator
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": isEn ? "Home" : "Ana Sayfa",
            "item": isEn ? "https://www.we24agency.com/en" : "https://www.we24agency.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": isEn ? "Insights" : "İçgörüler",
            "item": isEn ? "https://www.we24agency.com/en/insights" : "https://www.we24agency.com/icgoruler"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": insight.title,
            "item": canonicalUrl
          }
        ]
      },
      {
        "@type": "Article",
        "headline": insight.title,
        "description": insight.metaDescription,
        "image": `https://www.we24agency.com${insight.coverImage}`,
        "datePublished": insight.publishDate,
        "dateModified": "2026-08-27",
        "inLanguage": isEn ? "en" : "tr",
        "author": {
          "@type": "Person",
          "name": insight.author.name,
          "jobTitle": insight.author.role
        },
        "publisher": {
          "@type": "Organization",
          "name": "WE24 Agency",
          "url": "https://www.we24agency.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.we24agency.com/favicon.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl
        }
      },
      ...(insight.faqs && insight.faqs.length > 0 ? [{
        "@type": "FAQPage",
        "mainEntity": insight.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }] : [])
    ]
  };

  return (
    <div className="bg-[#030811] text-foreground min-h-screen">
      <Helmet>
        <title>{`${insight.metaTitle}`}</title>
        <meta name="description" content={insight.metaDescription} />
        <meta name="keywords" content={insight.tags.join(', ')} />
        <meta name="author" content={insight.author.name} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="tr" href={alternateTr} />
        <link rel="alternate" hrefLang="en" href={alternateEn} />
        <link rel="alternate" hrefLang="x-default" href={alternateTr} />

        {/* OpenGraph */}
        <meta property="og:title" content={insight.title} />
        <meta property="og:description" content={insight.excerpt} />
        <meta property="og:image" content={`https://www.we24agency.com${insight.coverImage}`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="WE24 Agency" />
        <meta property="og:locale" content={isEn ? "en_US" : "tr_TR"} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={insight.title} />
        <meta name="twitter:description" content={insight.excerpt} />
        <meta name="twitter:image" content={`https://www.we24agency.com${insight.coverImage}`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaGraph)}
        </script>
      </Helmet>

      {/* Header & Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 border-b border-white/[0.08] bg-gradient-to-b from-[#061524] via-[#030811] to-[#030811] overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-[#ff7a63]/10 blur-[130px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          {/* Breadcrumbs link */}
          <Link
            to={isEn ? "/en/insights" : "/icgoruler"}
            className="inline-flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-white uppercase tracking-widest transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-[#ff7a63]" />
            {isEn ? "All Insights" : "Tüm İçgörülere Dön"}
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3.5 py-1 rounded-full bg-[#ff7a63]/15 text-[#ff7a63] border border-[#ff7a63]/30 text-xs font-bold uppercase tracking-wider">
              {insight.category}
            </span>
            <span className="text-xs text-zinc-400 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-zinc-500" />
              {insight.readTime}
            </span>
            <span className="text-xs text-zinc-500">•</span>
            <span className="text-xs text-zinc-400">{insight.publishDate}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.2] font-display">
            {insight.title}
          </h1>

          <p className="text-base sm:text-lg text-zinc-300 mt-6 leading-relaxed border-l-2 border-[#ff7a63] pl-4 italic">
            "{insight.excerpt}"
          </p>

          {/* Author Badge */}
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#ff7a63]/20 border border-[#ff7a63]/40 flex items-center justify-center text-[#ff7a63] font-bold text-sm">
                WE
              </div>
              <div>
                <span className="text-sm font-bold text-white block">{insight.author.name}</span>
                <span className="text-xs text-zinc-400">{insight.author.role}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Reading Container */}
      <section className="py-16 max-w-4xl mx-auto px-6">
        {/* Cover Photo */}
        <div className="aspect-video w-full rounded-3xl overflow-hidden bg-zinc-950 mb-12 shadow-2xl border border-white/10 relative">
          <img
            src={insight.coverImage}
            alt={insight.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Table of Contents Box */}
        <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900/60 border border-white/10 mb-12 backdrop-blur-sm">
          <span className="text-xs font-bold text-[#ff7a63] uppercase tracking-wider block mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            {isEn ? "Table of Contents" : "İçindekiler & Bölümler"}
          </span>
          <ul className="space-y-2.5 text-sm">
            {insight.sections.map((section, sIdx) => (
              <li key={sIdx} className="flex items-start gap-2">
                <span className="text-[#ff7a63] font-bold text-xs mt-0.5">{sIdx + 1}.</span>
                <span className="text-zinc-300 font-medium hover:text-white transition-colors">
                  {section.heading.replace(/^[0-9.]+\s*/, '')}
                </span>
              </li>
            ))}
            {insight.checklist && (
              <li className="flex items-start gap-2 pt-1">
                <span className="text-[#ff7a63] font-bold text-xs mt-0.5">•</span>
                <span className="text-zinc-300 font-medium">{insight.checklist.title}</span>
              </li>
            )}
          </ul>
        </div>

        {/* Content Sections */}
        <div className="space-y-12 text-zinc-300 leading-relaxed text-base sm:text-lg">
          {insight.sections.map((section, idx) => (
            <article key={idx} className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-display tracking-tight pt-4 border-t border-white/5">
                {section.heading}
              </h2>
              {section.subheading && (
                <h3 className="text-base sm:text-lg text-[#ff7a63] font-medium tracking-wide">
                  {section.subheading}
                </h3>
              )}

              {section.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="leading-relaxed text-zinc-300">
                  {para}
                </p>
              ))}

              {section.bulletPoints && section.bulletPoints.length > 0 && (
                <ul className="space-y-2.5 my-4 bg-zinc-900/40 p-5 sm:p-6 rounded-2xl border border-white/5">
                  {section.bulletPoints.map((point, bIdx) => (
                    <li key={bIdx} className="flex items-start gap-3 text-sm sm:text-base text-zinc-200">
                      <CheckCircle2 className="w-4 h-4 text-[#ff7a63] shrink-0 mt-1" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.callout && (
                <div className="my-6 p-6 rounded-2xl bg-gradient-to-r from-zinc-900 via-zinc-900/80 to-[#ff7a63]/10 border border-[#ff7a63]/30">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#ff7a63] uppercase tracking-wider mb-2">
                    <Sparkles className="w-4 h-4" />
                    {section.callout.title}
                  </div>
                  <p className="text-sm sm:text-base text-white font-medium leading-relaxed">
                    {section.callout.text}
                  </p>
                </div>
              )}
            </article>
          ))}

          {/* Checklist Box */}
          {insight.checklist && (
            <div className="mt-12 p-6 sm:p-8 rounded-3xl bg-zinc-900/70 border border-white/15 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-2xl bg-[#ff7a63]/20 flex items-center justify-center text-[#ff7a63]">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white font-display">
                    {insight.checklist.title}
                  </h3>
                  <span className="text-xs text-zinc-400">
                    {isEn ? "Actionable Playbook Items" : "Uygulanabilir Kontrol Maddeleri"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {insight.checklist.items.map((item, cIdx) => (
                  <div key={cIdx} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 text-sm sm:text-base text-zinc-200">
                    <span className="w-5 h-5 rounded-full bg-[#ff7a63]/20 text-[#ff7a63] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      {cIdx + 1}
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQs Accordion/List */}
          {insight.faqs && insight.faqs.length > 0 && (
            <div className="mt-16 pt-12 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs font-bold text-[#ff7a63] uppercase tracking-wider mb-3">
                <HelpCircle className="w-4 h-4" />
                {isEn ? "Frequently Asked Questions" : "Sıkça Sorulan Sorular"}
              </div>
              <h3 className="text-2xl font-bold text-white font-display mb-6">
                {isEn ? "Key Questions Answered" : "Bu Konuda Merak Edilenler"}
              </h3>

              <div className="space-y-4">
                {insight.faqs.map((faq, fIdx) => (
                  <div key={fIdx} className="p-5 sm:p-6 rounded-2xl bg-zinc-900/40 border border-white/10">
                    <h4 className="text-base sm:text-lg font-bold text-white mb-2">
                      {faq.question}
                    </h4>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="pt-8 border-t border-white/10 flex flex-wrap items-center gap-2">
            <span className="text-xs text-zinc-400 font-bold uppercase tracking-wider mr-2">
              {isEn ? "Tags:" : "Etiketler:"}
            </span>
            {insight.tags.map((tag, tIdx) => (
              <span key={tIdx} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300">
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Related Case Studies Grid */}
        {relatedProjects.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <span className="text-xs uppercase tracking-widest text-[#ff7a63] font-bold block mb-2">
              {isEn ? "Related Case Studies" : "İlgili Vaka Analizleri"}
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white font-display mb-8">
              {isEn ? "See These Strategies in Action" : "Bu Stratejilerin Hayata Geçtiği Projelerimiz"}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedProjects.map((p: any) => (
                <Link
                  key={p.id}
                  to={isEn ? `/en/projects/${p.slug}` : `/projeler/${p.slug}`}
                  className="group block bg-zinc-900/40 border border-white/10 hover:border-[#ff7a63]/40 rounded-2xl p-5 transition-all"
                >
                  <div className="aspect-video w-full rounded-xl overflow-hidden bg-zinc-950 mb-4 border border-white/5">
                    <img
                      src={p.coverImage}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span className="text-[11px] font-bold text-[#ff7a63] uppercase tracking-wider block mb-1">
                    {p.client}
                  </span>
                  <h4 className="text-base font-bold text-white group-hover:text-[#ff7a63] transition-colors leading-snug">
                    {p.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action Bar */}
        <div className="mt-20 p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-[#061524] via-zinc-900 to-[#030811] border border-white/15 text-center shadow-2xl">
          <h3 className="text-2xl sm:text-4xl font-extrabold text-white font-display">
            {isEn ? "Ready to Elevate Your Next Production?" : "Projeniz İçin Stratejik Bir Çözüm Ortağı mı Arıyorsunuz?"}
          </h3>
          <p className="text-sm sm:text-base text-zinc-300 mt-3 max-w-xl mx-auto leading-relaxed">
            {isEn
              ? "From cinematic commercials to high-stakes live broadcasting, let's turn your vision into reality."
              : "Halka arz reklam filmlerinden çok kameralı canlı yayın reji organizasyonlarına kadar markanızı tek çatı altında geleceğe taşıyalım."
            }
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to={isEn ? "/en/contact" : "/iletisim"}
              className="px-8 py-4 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all shadow-xl hover:scale-105 inline-flex items-center gap-2"
            >
              {isEn ? "Request a Proposal" : "Teklif Talebinde Bulunun"}
              <ArrowRight className="w-4 h-4 text-[#ff7a63]" />
            </Link>
            <Link
              to={isEn ? "/en/insights" : "/icgoruler"}
              className="px-8 py-4 bg-zinc-900 border border-white/20 text-white hover:bg-zinc-800 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all"
            >
              {isEn ? "All Insights" : "Tüm İçgörüler"}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightDetail;
